import { useState, useEffect, useCallback } from 'react';
import { refundAPI, patientDashboardAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Plus, CheckCircle, XCircle, Clock, IndianRupee, RotateCcw, User, AlertCircle, Lock } from 'lucide-react';
import { cn } from '../lib/utils';

const APPROVAL_THRESHOLD = 2000; // Refunds >= Rs 2000 must be approved by Finance team

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { bg: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  processed: { bg: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { bg: 'bg-red-100 text-red-700', icon: XCircle },
};

const RefundPage = () => {
  const { hospital, user } = useAuth();
  const userRoleCode = (user?.role_code || user?.role_name || '').toLowerCase().replace(/\s+/g, '_');
  const isFinance = user?.is_super_admin || ['accountant', 'finance', 'hospital_admin'].includes(userRoleCode);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (hospital?.id) params.hospital_id = hospital.id;
      if (filterStatus !== 'all') params.status = filterStatus;
      const res = await refundAPI.list(params);
      setRefunds(res.data || []);
    } catch { toast.error('Failed to load refunds'); }
    setLoading(false);
  }, [hospital?.id, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const filtered = refunds.filter(r => r.patient_name?.toLowerCase().includes(search.toLowerCase()));

  // Approval-rights helper: requester can never approve their own refund.
  // <Rs 2000 → any Billing/Finance/Admin (not requester). ≥Rs 2000 → Finance/Admin only.
  const canApprove = (r) => {
    if (r.requested_by_id && user?.id && r.requested_by_id === user.id) return false;
    if ((r.refund_amount || 0) >= APPROVAL_THRESHOLD) return isFinance;
    return true; // any billing/finance/admin user can approve smaller refunds
  };

  const handleApprove = async (r) => {
    try {
      await refundAPI.approve(r.id, {
        approved_by: `${user?.first_name} ${user?.last_name}`,
        approved_by_id: user?.id,
      });
      toast.success('Refund approved'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve'); }
  };

  const handleProcess = async (id) => {
    try { await refundAPI.process(id); toast.success('Refund processed'); load(); }
    catch { toast.error('Failed'); }
  };

  const handleReject = async (id) => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;
    try { await refundAPI.reject(id, { reason }); toast.success('Refund rejected'); load(); }
    catch { toast.error('Failed'); }
  };

  const totalPending = refunds.filter(r => r.status === 'pending').length;
  const totalProcessed = refunds.filter(r => r.status === 'processed').reduce((a, r) => a + (r.refund_amount || 0), 0);

  return (
    <div className="space-y-5" data-testid="refund-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Refund Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">{refunds.length} refund requests</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="new-refund-btn">
          <Plus size={16} className="mr-1.5" /> New Refund
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-lg font-bold">{refunds.length}</p><p className="text-[10px] text-slate-500 uppercase">Total Requests</p></div>
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-4"><p className="text-lg font-bold text-amber-700">{totalPending}</p><p className="text-[10px] text-amber-600 uppercase">Pending</p></div>
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4"><p className="text-lg font-bold text-emerald-700">{refunds.filter(r => r.status === 'processed').length}</p><p className="text-[10px] text-emerald-600 uppercase">Processed</p></div>
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4"><p className="text-lg font-bold text-emerald-700 flex items-center"><IndianRupee size={14} />{totalProcessed.toLocaleString()}</p><p className="text-[10px] text-emerald-600 uppercase">Total Refunded</p></div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search by patient name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 h-10" />
          </div>
          <div className="flex gap-1.5">
            {['all', 'pending', 'approved', 'processed', 'rejected'].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className={cn('px-3 py-1.5 rounded-full text-xs font-semibold capitalize border transition-all',
                  filterStatus === s ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {loading ? <div className="text-center py-12 text-slate-400">Loading...</div> :
        filtered.length === 0 ? <div className="text-center py-12 text-slate-400"><RotateCcw size={32} className="mx-auto mb-2 opacity-30" />No refund requests</div> :
        <div className="space-y-3">
          {filtered.map(r => {
            const st = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
            const StIcon = st.icon;
            return (
              <div key={r.id} className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 hover:border-slate-200 transition-all" data-testid={`refund-${r.id}`}>
                <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center', st.bg)}>
                  <StIcon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800">{r.patient_name || 'Unknown'}</p>
                  <p className="text-xs text-slate-500">{r.reason || 'No reason'} &middot; {new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-800 flex items-center justify-end"><IndianRupee size={12} />{r.refund_amount?.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-400">of <IndianRupee size={9} className="inline" />{r.original_amount?.toLocaleString()}</p>
                </div>
                <span className={cn('text-[10px] px-2.5 py-1 rounded-full font-semibold capitalize', st.bg)}>{r.status}</span>
                <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                  {r.status === 'pending' && (
                    <>
                      {canApprove(r) ? (
                        <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => handleApprove(r)} data-testid={`refund-approve-${r.id}`}>Approve</Button>
                      ) : (
                        <div className="h-7 px-2 inline-flex items-center gap-1 text-[10px] text-slate-400 bg-slate-50 rounded border border-slate-200" title={(r.refund_amount || 0) >= APPROVAL_THRESHOLD ? 'Refunds ≥ Rs 2000 need Finance approval' : 'Requester cannot approve their own refund'}>
                          <Lock size={10} /> {(r.refund_amount || 0) >= APPROVAL_THRESHOLD ? 'Finance only' : 'Awaits peer'}
                        </div>
                      )}
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-500 border-red-200" onClick={() => handleReject(r.id)}>Reject</Button>
                    </>
                  )}
                  {r.status === 'approved' && (
                    <Button size="sm" className="h-7 px-2 bg-blue-600 hover:bg-blue-700 text-xs" onClick={() => handleProcess(r.id)}>Process</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>}
      </div>

      <RefundModal open={showModal} onClose={() => setShowModal(false)} hospitalId={hospital?.id} user={user} onSaved={load} />
    </div>
  );
};

const RefundModal = ({ open, onClose, hospitalId, user, onSaved }) => {
  const [form, setForm] = useState({ patient_id: '', patient_name: '', original_amount: '', refund_amount: '', refund_type: 'full', reason: '' });
  const [saving, setSaving] = useState(false);
  const [patientQuery, setPatientQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Patient typeahead — search Patient registry by name / UHID / phone
  useEffect(() => {
    if (!open) return;
    if (patientQuery.trim().length < 2) { setResults([]); return; }
    let abort = false;
    setSearching(true);
    patientDashboardAPI.search(patientQuery).then(res => {
      if (abort) return;
      setResults(res.data || []);
    }).catch(() => {/* ignore */}).finally(() => !abort && setSearching(false));
    return () => { abort = true; };
  }, [patientQuery, open]);

  const pickPatient = (p) => {
    setForm(f => ({ ...f, patient_id: p.id, patient_name: `${p.first_name} ${p.last_name}` }));
    setPatientQuery(`${p.first_name} ${p.last_name}`);
    setResults([]);
  };

  const handleSave = async () => {
    if (!form.patient_id) { toast.error('Please select a patient from the search results'); return; }
    if (!form.refund_amount || parseFloat(form.refund_amount) <= 0) { toast.error('Enter a valid refund amount'); return; }
    setSaving(true);
    try {
      await refundAPI.create({
        ...form,
        original_amount: parseFloat(form.original_amount) || 0,
        refund_amount: parseFloat(form.refund_amount) || 0,
        hospital_id: hospitalId,
        requested_by: `${user?.first_name} ${user?.last_name}`,
        requested_by_id: user?.id,
      });
      toast.success('Refund request created');
      onSaved(); onClose();
      setForm({ patient_id: '', patient_name: '', original_amount: '', refund_amount: '', refund_type: 'full', reason: '' });
      setPatientQuery(''); setResults([]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const amt = parseFloat(form.refund_amount) || 0;
  const needsFinance = amt >= APPROVAL_THRESHOLD;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New Refund Request</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Patient Search */}
          <div>
            <Label>Patient *</Label>
            {form.patient_id ? (
              <div className="flex items-center justify-between gap-2 p-3 bg-teal-50 border border-teal-200 rounded-lg" data-testid="refund-selected-patient">
                <div className="flex items-center gap-2 min-w-0">
                  <User size={14} className="text-teal-700 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-800 truncate">{form.patient_name}</span>
                </div>
                <button onClick={() => { setForm(f => ({ ...f, patient_id: '', patient_name: '' })); setPatientQuery(''); }} className="text-xs text-slate-400 hover:text-red-500">Change</button>
              </div>
            ) : (
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={patientQuery} onChange={e => setPatientQuery(e.target.value)} placeholder="Search by name, UHID, or phone..." className="pl-9 h-10" data-testid="refund-patient-search" autoFocus />
                {patientQuery.length >= 2 && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {searching ? <div className="px-3 py-2 text-xs text-slate-400">Searching...</div> :
                     results.length === 0 ? <div className="px-3 py-2 text-xs text-slate-400">No patients found</div> :
                     results.map(p => (
                      <button key={p.id} onClick={() => pickPatient(p)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 flex items-center gap-3" data-testid={`refund-patient-${p.id}`}>
                        <User size={14} className="text-slate-400" />
                        <div>
                          <p className="text-xs font-medium text-slate-800">{p.first_name} {p.last_name}</p>
                          <p className="text-[10px] text-slate-400">{p.patient_id} &middot; {p.phone}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Original Amount</Label><Input type="number" value={form.original_amount} onChange={e => up('original_amount', e.target.value)} /></div>
            <div><Label>Refund Amount *</Label><Input type="number" value={form.refund_amount} onChange={e => up('refund_amount', e.target.value)} data-testid="refund-amount" /></div>
          </div>

          {amt > 0 && (
            <div className={cn('text-[11px] px-3 py-2 rounded-lg flex items-start gap-2 border',
              needsFinance ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-blue-50 text-blue-800 border-blue-200')} data-testid="refund-approval-note">
              <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
              <span>
                {needsFinance
                  ? <>Refunds ≥ <strong>Rs 2,000</strong> require approval from the <strong>Finance team</strong>.</>
                  : <>Refunds below Rs 2,000 require approval from another Billing/Finance staff member (you cannot approve your own request).</>}
              </span>
            </div>
          )}

          <div><Label>Refund Type</Label>
            <Select value={form.refund_type} onValueChange={v => up('refund_type', v)}><SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="full">Full Refund</SelectItem><SelectItem value="partial">Partial Refund</SelectItem></SelectContent>
            </Select></div>
          <div><Label>Reason</Label><Input value={form.reason} onChange={e => up('reason', e.target.value)} placeholder="Reason for refund" /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-refund-btn">{saving ? 'Saving...' : 'Submit Request'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RefundPage;
